/**
 * Pre-screen risk-flag banner (client 2026-08-27) — the flags repeated at the
 * TOP of the report, above the applicant card, so a pre-screen landlord meets
 * them before anything else. The body keeps its regular "Risk Flags & Alerts"
 * card; this is an additional surface, not a move.
 *
 * Pre-screen ONLY: full screening already has a dedicated place for flags
 * (the section, plus the score/verification surfaces around it), so repeating
 * them there would be noise.
 *
 * No heading — the flag rows carry themselves, matching the regular panels'
 * icon + message anatomy. Dismissible for the current view only: the landlord
 * can clear it to read the report, and a reload brings it back, so a real
 * problem can't be permanently hidden (plain component state, no persistence).
 */
import { FlagTriangleRight, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '#app/utils/misc'
import { DETAIL_CONTAINER } from './primitives'

export function PreScreenFlagBanner({
	red,
	yellow,
	onRequestBankAccount,
}: {
	red: string[]
	yellow: string[]
	/** Wired ONLY when the viewer may request a bank account AND a flag is
	 * actually about banking — the CTA would be a non-sequitur otherwise. */
	onRequestBankAccount?: () => void
}) {
	const [dismissed, setDismissed] = useState(false)
	const messages = [...red, ...yellow]
	if (dismissed || messages.length === 0) return null

	return (
		<div className={cn(DETAIL_CONTAINER, 'mt-6')}>
			{/* Right padding reserves the dismiss ✕'s column: with a single-line
			    flag the CTA used to run right up under the absolutely-positioned
			    ✕ (client 2026-09-09) — now the content always stops short of it. */}
			<div className="relative flex flex-wrap items-center gap-4 rounded-3xl bg-[#FFF7F7] p-6 pr-[60px] sm:gap-6 sm:p-7 sm:pr-[60px] xl:flex-nowrap">
				{/* Messages take at most ~72% of the row on wide screens (client
				    2026-09-09) so the CTA + dismiss ✕ always have room beside them
				    instead of the text pushing the button into the corner. */}
				<ul className="min-w-0 flex-1 space-y-3 xl:max-w-[72%]">
					{messages.map((message) => (
						<li
							key={message}
							className="flex items-start gap-2.5 text-[15px] text-[#747474]"
						>
							<FlagTriangleRight
								aria-hidden="true"
								className="mt-1 size-4 shrink-0 text-[#EF4343]"
							/>
							<span className="flex-1">{message}</span>
						</li>
					))}
				</ul>
				{onRequestBankAccount ? (
					<button
						type="button"
						onClick={onRequestBankAccount}
						// ml-auto hugs the CTA to the right edge, just inside the ✕'s
						// reserved column, instead of floating mid-row (client 2026-09-09).
						className="h-12 shrink-0 cursor-pointer rounded-full bg-[#32302F] px-6 text-sm font-medium text-white transition-colors hover:bg-[#4a4746] xl:ml-auto"
					>
						Request Additional Bank Account
					</button>
				) : null}
				{/* Sits clear of the CTA so the two never overlap when they wrap. */}
				<button
					type="button"
					onClick={() => setDismissed(true)}
					aria-label="Dismiss risk flags"
					className="absolute top-4 right-4 cursor-pointer rounded-full p-1 text-[#32302F] transition-colors hover:bg-[#00000008]"
				>
					<X aria-hidden="true" className="size-5" />
				</button>
			</div>
		</div>
	)
}
